import { useCallback, useEffect, useMemo, useState } from 'react';
import { format, parse as parseDateFns } from 'date-fns';
import { DateObject } from 'react-multi-date-picker';
import styled from 'styled-components';

import { CalendarIcon } from '@assets/icons/CalendarIcon';
import { Dropdown } from '@components/Dropdown';
import { MultiDatePicker } from '@components/MultiDatePicker';
import { UnstyledButton } from '@components/UnstyledButton/UnstyledButton';

import { FilterCollapse } from '../components/FilterCollapse';
import { useJobsBoardFiltersModalContext } from '../context';

export const WorkDatesFilter = () => {
  const { modalFilters, setWorkDates } = useJobsBoardFiltersModalContext();

  const calendarTitle = useMemo(() => {
    if (!modalFilters?.workDates?.length) {
      return 'All Dates';
    }

    return modalFilters.workDates
      .map((date) => {
        return format(parseDateFns(date, 'yyyy-MM-dd', new Date()), 'M/d/yy');
      })
      .join(', ');
  }, [modalFilters?.workDates]);

  const [isPopUpOpen, setIsPopUpOpen] = useState(false);

  return (
    <FilterCollapse
      title={'Work Dates'}
      subtitle={calendarTitle}
      content={
        <StyledContainer>
          <StyledDropdown
            title={
              <>
                <div className='work-dates-filter-title'>{calendarTitle}</div>
                <div className='icon-container'>
                  <CalendarIcon />
                </div>
              </>
            }
            trigger={['click']}
            destroyPopupOnHide
            open={isPopUpOpen}
            onOpenChange={setIsPopUpOpen}
            dropdownRender={() => (
              <DropdownRenderDate
                modalFilterWorkDates={modalFilters.workDates}
                setModalFilterWorkDates={setWorkDates}
                setIsPopUpOpen={setIsPopUpOpen}
              />
            )}
          ></StyledDropdown>
        </StyledContainer>
      }
    />
  );
};

type DropdownRenderDateProps = {
  modalFilterWorkDates: string[];
  setModalFilterWorkDates: (workDates: string[]) => void;
  setIsPopUpOpen: (isPopUpOpen: boolean) => void;
};
const DropdownRenderDate = ({
  modalFilterWorkDates,
  setModalFilterWorkDates,
  setIsPopUpOpen,
}: DropdownRenderDateProps) => {
  const [localWorkDates, setLocalWorkDates] = useState<string[]>(
    modalFilterWorkDates || []
  );
  useEffect(() => {
    setLocalWorkDates(modalFilterWorkDates || []);
  }, [modalFilterWorkDates]);
  const clearLocalWorkDates = useCallback(() => {
    setLocalWorkDates([]);
  }, []);
  const applyLocalWorkDates = useCallback(() => {
    setModalFilterWorkDates(localWorkDates);
    setIsPopUpOpen(false);
  }, [localWorkDates, setModalFilterWorkDates, setIsPopUpOpen]);

  const isClearButtonDisabled = useMemo(() => {
    return !localWorkDates?.length;
  }, [localWorkDates]);
  return (
    <MultiDatePicker
      plugins={[
        <DatePickerFooter
          clearLocalWorkDates={clearLocalWorkDates}
          applyLocalWorkDates={applyLocalWorkDates}
          position='top'
          key='date-picker-footer'
          isClearButtonDisabled={isClearButtonDisabled}
        />,
      ]}
      key='date-picker-footer'
      onChange={(dateValues) => {
        const dateStrings =
          (dateValues as unknown as DateObject[])?.map((date) =>
            date?.format('YYYY-MM-DD')
          ) || [];
        setLocalWorkDates(dateStrings);
      }}
      value={localWorkDates}
    />
  );
};

type DatePickerFooterProps = {
  clearLocalWorkDates: () => void;
  applyLocalWorkDates: () => void;
  isClearButtonDisabled: boolean;
};

const DatePickerFooter = ({
  clearLocalWorkDates,
  applyLocalWorkDates,
  isClearButtonDisabled,
}: DatePickerFooterProps) => {
  return (
    <StyledDatePickerFooter>
      <UnstyledButton
        className='footer-button'
        onClick={clearLocalWorkDates}
        disabled={isClearButtonDisabled}
      >
        Clear
      </UnstyledButton>
      <UnstyledButton className='footer-button' onClick={applyLocalWorkDates}>
        Done
      </UnstyledButton>
    </StyledDatePickerFooter>
  );
};

const StyledDatePickerFooter = styled.div`
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  color: ${({ theme }) => theme.palette.primary[0]};
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;

  .footer-button {
    padding: 10px 16px;
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const StyledContainer = styled.div`
  padding: 8px 0;
`;

const StyledDropdown = styled(Dropdown)`
  &.ant-btn.ant-btn-default.ant-dropdown-trigger {
    width: 100% !important;
    height: 32px;
    border: 1px solid ${({ theme }) => theme.palette.stroke[0]};
    background: #ffffff;
    padding: 7px 11px;
    display: flex;
    align-items: center;
    justify-content: center;

    > span {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-direction: row;
      width: 100%;

      .icon-container {
        width: 16px;
        height: 16px;
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
          width: 13.6px;
          height: auto;
        }
      }

      .work-dates-filter-title {
        font-size: 12px;
        line-height: 16px;
        font-weight: 400;

        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        max-width: calc(100% - 20px);
      }
    }
  }
`;
